import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { projectFinancialsApi } from '../api';
import { FinancialSummaryView } from './FinancialSummaryView';
import { FinancialPie } from './FinancialPie';
import { ProjectReceiptsSection } from './ProjectReceiptsSection';

// Simplified Project Financials: one summary, one pie, receipts underneath.
export const ProjectFinancialsTab = ({ project }) => {
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    projectFinancialsApi
      .get(project.id)
      .then((r) => setFin(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const goTab = (tab) => () => navigate(`?tab=${tab}`);
  const linkFor = (key) => ({
    budget: goTab('overview'),
    change_orders: goTab('quote-change-orders'),
    materials: goTab('expenses-receipts'),
    reimbursements: goTab('expenses-receipts'),
    employees: goTab('labor'),
    subs: goTab('subcontractors'),
  }[key]);

  if (loading) {
    return (
      <div className="text-center py-16 text-gray-500" data-testid="financials-loading">
        <Loader2 size={24} className="mx-auto mb-2 animate-spin text-[#D4AF37]" /> Loading financials…
      </div>
    );
  }
  if (!fin) return <p className="text-gray-500 text-sm py-8">Could not load financials.</p>;

  return (
    <div className="space-y-6" data-testid="project-financials-simple">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <FinancialSummaryView fin={fin} linkFor={linkFor} titleNote="Tap any line to manage the records behind it." />
        <FinancialPie
          materialsNet={(fin.materials_misc_cents || 0) - (fin.reimbursements_cents || 0)}
          employees={fin.employee_costs_cents}
          subs={fin.subcontractor_costs_cents}
          profit={fin.potential_gross_profit_cents}
        />
      </div>
      <ProjectReceiptsSection project={project} onFinancialsChanged={load} />
    </div>
  );
};
